import budgetLogo from "@assets/logo-budget2024-v3.svg";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import SGBudget2024Banner from "@components/Banner/SGBudget2024Banner";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import { GADataProps } from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import KeywordFilter from "@features/mybt/components/KeywordFilter";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { useInjectClientData } from "@hooks/useInjectClientData";
import TemplateI from "@particles/card/templates/TemplateI/TemplateI";
import Listing from "@particles/listing/Listing";
import Telegram from "@routes/SingaporeBudget/2024/components/Telegram";
import Videos from "@routes/SingaporeBudget/2024/components/Videos";
import LatestStories from "@routes/SingaporeBudget/components/LatestStories/LatestStories";
import ViewsStories from "@routes/SingaporeBudget/components/ViewsStories/ViewsStories";
import { Budget2024Data } from "@routes/SingaporeBudget/utils/types";

import Booklet from "./components/Booklet";
import Infographics from "./components/Infographics";

/**
 * SingaporeBudget2024 component to display the Singapore Budget 2024 page.
 */
const SingaporeBudget2024 = ({
  highlights,
  latestNews,
  views,
  relatedStories,
  seoFooterData,
  entity: { tag },
  gaProps,
}: Budget2024Data & { gaProps: GADataProps }): React.ReactElement => {
  useInjectClientData({ gaProps });

  return (
    <div className="w-full" data-testid="singapore-budget-2024-component">
      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header />

      <PulseEntry />

      <Container rootClassName="pt-6">
        <div className="mb-6 flex items-center justify-between">
          <KeywordFilter tag={tag} className="inline-block bg-gray-250 p-3" />

          <img
            src={budgetLogo}
            width={43}
            height={60}
            alt="Singapore Budget 2024"
          />
        </div>

        <div className="mb-10 grid lg:grid-cols-12">
          <div className="lg:col-span-8">
            <TemplateI cardProps={highlights} />
          </div>

          <div className="lg:col-span-4 lg:pl-6">
            <Booklet rootClassName="border-top border-gray-850 pt-2 mb-10" />

            <LatestStories
              cardProps={latestNews}
              className="mb-6 border-t border-gray-850 pt-2"
            />

            <Infographics rootClassName="border-top border-gray-850 pt-2" />
          </div>
        </div>

        <SGBudget2024Banner
          copyText="Unlock all of BT's coverage on Budget 2024."
          link="https://subscribe.sph.com.sg/promotions/bt-personal/?utm_campaign=btbudget24&utm_medium=sph-publication&utm_source=banner&utm_content=btp99-banner-microsite"
          buttonText="SUBSCRIBE NOW"
          buttonClass="bg-orange-300 border-orange-300 !px-4"
          imgClass="hidden xs:block"
          headerClass="xs:ml-2 xs:border-l xs:border-gray-850 xs:border-opacity-20 xs:pl-2"
          rootClassName="sm:px-4 mb-10"
        />

        <div className="mb-10 grid lg:grid-cols-12">
          <div className="lg:col-span-8 lg:pr-3">
            <ViewsStories
              cardProps={views}
              className="border-t border-gray-850 pt-2"
            />
          </div>

          <div className="lg:col-span-4 lg:pl-6">
            <Telegram />
          </div>
        </div>

        <Videos rootClassName="border-t border-gray-850 pt-2 mb-10" />

        <div className="mb-10 grid lg:grid-cols-12">
          <div className="border-t border-gray-850 pt-2 lg:col-span-8 lg:pr-3">
            <BlockTitle text="RELATED STORIES" />

            <Listing
              cardProps={relatedStories}
              pageType={"keyword"}
              size={10}
              urlPath={tag.urlPath}
              offset={3}
            />
          </div>

          <div className="lg:col-span-4 lg:pl-6">
            <Ad
              adType="imu2"
              className="flex items-center justify-center lg:sticky lg:top-[70px]"
            />
          </div>
        </div>
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default SingaporeBudget2024;
